import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApplication } from '../services/firebaseService';
import { Application } from '../types';

const LabeledRow: React.FC<{ label: string; value?: string; wide?: boolean }> = ({ label, value = '', wide }) => (
  <div className="flex text-sm mb-2">
    <div className="w-48 font-semibold">{label}</div>
    <div className={`flex-1 border-b border-black ${wide ? 'pb-1' : 'pb-0.5'}`}>{value}</div>
  </div>
);

const ApplicationPrintPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      const app = await getApplication(id);
      setApplication(app);
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!application) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center space-y-4">
        <div>Application not found.</div>
        <button className="px-4 py-2 bg-gray-800 text-white rounded" onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }

  const addr = application.address || ({} as any);
  const photoUrl = application.documents?.photo;

  return (
    <div className="min-h-screen bg-white text-black px-6 py-6 print:px-4" style={{ fontFamily: 'Arial, sans-serif' }}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-start space-x-3">
          <img src="/logo192.png" alt="Kreed Odisha" className="w-14 h-14 object-contain" />
          <div>
            <h1 className="text-2xl font-bold">KREED ODISHA - 2025</h1>
            <p className="text-xs">Managed by Hardini Dynamics Pvt. Ltd.</p>
            <p className="text-xs">Plot No. 233, Aryan Vihar, Sundarpada, Bhubaneswar, Odisha - 751002</p>
          </div>
        </div>
        <div className="w-24 h-28 border border-black flex items-center justify-center overflow-hidden">
          {photoUrl ? (
            <img src={photoUrl} alt="Applicant" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs text-center px-1">PHOTO</span>
          )}
        </div>
      </div>

      <div className="text-center font-semibold bg-black text-white py-1 mb-4">APPLICATION FORM</div>

      <div className="space-y-3 text-sm">
        <LabeledRow label="1. Name of the Applicant" value={`${application.firstName} ${application.lastName}`} wide />
        <LabeledRow label="2. Name of the Father" value={application.fatherName} wide />
        <LabeledRow label="3. Name of the Mother" value={application.motherName} wide />
        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="4. Date of Birth" value={application.dateOfBirth} />
          <LabeledRow label="Sex" value={application.gender} />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <LabeledRow label="5. Nationality" value={application.nationality} />
          <LabeledRow label="Religion" value={application.religion} />
          <LabeledRow label="Caste" value={application.caste} />
        </div>
        <LabeledRow label="6. Aadhaar No." value={application.aadhaarNumber} />
        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="7. Address At" value={addr.street} />
          <LabeledRow label="Po" value={addr.postOffice} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="Ps" value={addr.policeStation} />
          <LabeledRow label="Block" value={addr.block} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="Dist" value={addr.district} />
          <LabeledRow label="Odisha, PIN" value={addr.pincode} />
        </div>

        <div>
          <div className="font-semibold mb-1">8. Educational qualification:</div>
          <div className="text-xs mb-2">Stream / Board-University / Passing Year / Percentage</div>
          <div className="space-y-1 text-sm">
            <div>10th: {application.education?.tenth?.board || ''} | {application.education?.tenth?.year || ''} | {application.education?.tenth?.percentage || ''}% | {application.education?.tenth?.school || ''}</div>
            <div>12th: {application.education?.twelfth?.board || ''} | {application.education?.twelfth?.year || ''} | {application.education?.twelfth?.percentage || ''}% | {application.education?.twelfth?.school || ''}</div>
            {application.education?.graduation && (
              <div>Graduation: {application.education?.graduation?.degree || ''} | {application.education?.graduation?.university || ''} | {application.education?.graduation?.year || ''} | {application.education?.graduation?.percentage || ''}%</div>
            )}
            {application.education?.bped && (
              <div>BPED: {application.education?.bped?.university || ''} | {application.education?.bped?.year || ''} | {application.education?.bped?.percentage || ''}%</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="9. Height" value={`${application.heightCm || ''} cm`} />
          <LabeledRow label="Weight" value={`${application.weightKg || ''} Kg`} />
        </div>
        <LabeledRow label="9. Sports Category" value={application.sportsCategory || ''} />
        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="10. Events State Level" value={application.eventsStateLevel ? 'Yes' : 'No'} />
          <LabeledRow label="National Level" value={application.eventsNationalLevel ? 'Yes' : 'No'} />
        </div>
        <LabeledRow label="11. NCC" value={application.nccGrade || ''} />
        <div className="grid grid-cols-2 gap-4">
          <LabeledRow label="12. Mobile" value={application.mobileNumber} />
          <LabeledRow label="Email" value={application.emailId} />
        </div>

        <div className="mt-6 text-sm">
          Having gone through the guidelines I agree to abide by all the rules & regulations and also undertake that all the information above are true to my knowledge.
        </div>
        <div className="flex justify-between mt-6 text-sm">
          <div>Date: __________________</div>
          <div className="text-right">Signature of the Applicant</div>
        </div>

        <div className="border-t border-dashed mt-6 pt-4 text-sm">
          <div className="font-semibold mb-2">FOR OFFICE USE ONLY</div>
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              <span>Date of Application</span>
              <span className="border-b border-black w-40"></span>
            </div>
            <div className="flex items-center space-x-2">
              <span>Ref. No</span>
              <span className="border-b border-black w-32"></span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex space-x-3 no-print">
        <button onClick={() => window.print()} className="px-4 py-2 bg-primary-600 text-white rounded">Print</button>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-700 text-white rounded">Back</button>
      </div>
    </div>
  );
};

export default ApplicationPrintPage;
